import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Gauge, Boxes, ClipboardList, CalendarCheck, Wrench, PackageSearch, FileBarChart, ArrowLeft, Lock, Sparkles, Activity, Microscope } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { NAVY, CARD, GOLD, MUTED, OmCard } from "@/components/om/OmBits";

const NAV = [
  [Gauge, "Command Centre", "/om/dashboard"],
  [Boxes, "Asset Register", "/om/assets"],
  [ClipboardList, "Daily Monitoring", "/om/daily-log"],
  [CalendarCheck, "Preventive (PM)", "/om/preventive-maintenance"],
  [Wrench, "Corrective (CM)", "/om/corrective-maintenance"],
  [PackageSearch, "Spares & Inventory", "/om/inventory"],
  [Activity, "Reliability", "/om/reliability"],
  [Microscope, "RCA Library", "/om/rca"],
  [FileBarChart, "Reports", "/om/reports"],
];

export default function OmLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    api
      .get("/om/meta")
      .then(({ data }) => setState({ loading: false, meta: data }))
      .catch((e) => setState({ loading: false, locked: e.response?.status === 402 }));
  }, []);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-[13px]" style={{ background: NAVY, color: MUTED }}>Opening O&M…</div>;
  }

  if (state.locked) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-14" style={{ background: NAVY }} data-testid="om-locked-preview">
        <div className="max-w-lg w-full text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 mb-5" style={{ background: `${GOLD}1F`, borderRadius: 999 }}>
            <Lock className="w-6 h-6" style={{ color: GOLD }} />
          </div>
          <h1 className="text-white font-bold" style={{ fontSize: 34, letterSpacing: "-0.01em" }}>
            Operations &amp; Maintenance
          </h1>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: MUTED }}>
            The O&amp;M platform — asset register with health scoring, daily shift logs, preventive schedules,
            SLA-tracked fault ticketing, spare parts control and compliance reporting — is available on the
            <strong className="text-white"> Professional</strong> and <strong className="text-white">Enterprise</strong> plans.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-7 text-left">
            {[["Asset health", "Live 0-100 score per asset"], ["PM compliance", "Auto work orders & sign-off"], ["SLA tickets", "4h to 7d by severity"]].map(([t, d]) => (
              <OmCard key={t}>
                <Sparkles className="w-4 h-4" style={{ color: GOLD }} />
                <div className="text-white text-[13px] font-medium mt-2">{t}</div>
                <div className="text-[11.5px] mt-0.5" style={{ color: MUTED }}>{d}</div>
              </OmCard>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Button onClick={() => navigate("/pricing")} className="font-semibold" style={{ background: GOLD, color: NAVY, borderRadius: 8, padding: "12px 28px" }} data-testid="om-upgrade-btn">
              Upgrade to unlock O&amp;M
            </Button>
            <Link to="/om/sectors">
              <Button variant="outline" style={{ borderColor: GOLD, color: GOLD, background: "transparent", borderRadius: 8, padding: "12px 28px" }} data-testid="om-sectors-btn">
                See it by sector
              </Button>
            </Link>
          </div>
          <button onClick={() => navigate("/projects")} className="mt-6 text-[12.5px] hover:underline" style={{ color: MUTED }} data-testid="om-back-btn">
            ← Back to my projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid="om-layout">
      <aside className="hidden lg:flex flex-col w-[236px] shrink-0 border-r" style={{ borderColor: "rgba(201,168,76,0.16)", background: CARD }}>
        <div className="h-14 flex items-center px-4 border-b" style={{ borderColor: "rgba(201,168,76,0.16)" }}>
          <Link to="/projects" className="flex items-baseline" data-testid="om-logo-link">
            <span className="text-white font-bold text-[15px]">Plan</span>
            <span className="font-bold text-[15px]" style={{ color: GOLD }}>-</span>
            <span className="text-white font-bold text-[15px]">Sarthi</span>
          </Link>
          <span className="ml-2 text-[9.5px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>O&amp;M</span>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map(([Icon, label, to]) => (
            <NavLink
              key={to}
              to={to}
              className="flex items-center gap-2.5 px-4 py-2.5 text-[13px] transition-colors duration-150"
              style={({ isActive }) => ({
                color: isActive ? GOLD : "rgba(255,255,255,0.82)",
                background: isActive ? `${GOLD}14` : "transparent",
                borderLeft: `2px solid ${isActive ? GOLD : "transparent"}`,
              })}
              data-testid={`om-nav-${to.split("/").pop()}`}
            >
              <Icon className="w-4 h-4 shrink-0" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "rgba(201,168,76,0.16)" }}>
          <div className="text-[12.5px] text-white truncate">{user?.name}</div>
          <div className="text-[11px]" style={{ color: MUTED }}>{state.meta?.role} · {state.meta?.can_write ? "read/write" : "read-only"}</div>
          <Link to="/projects" className="inline-flex items-center gap-1 mt-3 text-[12px] hover:underline" style={{ color: GOLD }} data-testid="om-exit-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Construction phase
          </Link>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="lg:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b" style={{ borderColor: "rgba(201,168,76,0.16)", background: CARD }}>
          {NAV.map(([Icon, label, to]) => (
            <NavLink
              key={to}
              to={to}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11.5px] whitespace-nowrap"
              style={({ isActive }) => ({ color: isActive ? GOLD : MUTED, background: isActive ? `${GOLD}14` : "transparent", borderRadius: 6 })}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </NavLink>
          ))}
        </div>
        <main className="p-4 sm:p-6 max-w-[1500px]">
          <Outlet context={{ meta: state.meta }} />
        </main>
      </div>
    </div>
  );
}
